import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Divider, Paper, Button, Grid2, IconButton, Chip, Tooltip, Popover, List, ListItem, ListItemText } from '@mui/material';
import { PackageFormValues } from './packageFormHelpers';
import styles from '../CounselingDetails/CounselingDetails.module.css';
import { ReactComponent as Edit } from '../../../../../assets/SVG/Edit.svg';
import { useNavigate } from 'react-router-dom';
import { usePatientContext } from '../../../../../components/SubPatinetProvider';
import { useTranslation } from 'react-i18next';
import { convertUnderscoreToTitle } from '../../../../../utils/commonUtils';
import useCurrency from '../../../../../components/CurrencyProvider';
import TruncatedText from '../../../../../utils/TruncatedText';


const TAB_BUTTON_BASE_SX = {
  borderRadius: 2,
  px: 2,
  py: 0.75,
  fontWeight: 600,
  fontSize: 14,
  backgroundColor: '#fff',
} as const;

const GRID_SIZES = {
  serviceField: { xl: 2, lg: 2, md: 4, xs: 6 },
  small: { xl: 2, lg: 2, md: 3, xs: 6 },
  medium: { xl: 3, lg: 3, md: 4, xs: 6 },
  large: { xl: 3, lg: 4, md: 6, xs: 12 },
  remark: { xl: 3, lg: 4, md: 12, xs: 12 },
  cardField: { xs: 6, sm: 6, md: 2 }
} as const;

const toLabel = (value: unknown, fallback = '-'): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const maybeName = (value as { name?: unknown }).name;
    if (typeof maybeName === 'string' || typeof maybeName === 'number' || typeof maybeName === 'boolean') {
      return String(maybeName);
    }
  }
  return fallback;
};

interface Props {
  data: PackageFormValues;
  onBack?: () => void;
}

const PackageDetailsViewMode: React.FC<Props> = ({ data, onBack }) => {
  const pd = data.package_details;
  const navigate = useNavigate();
  const { patientId } = usePatientContext();
  const { t: tBilling } = useTranslation('billing_config');
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = React.useState<'services' | 'pharmacy'>('services');

  // Popover state for "Service From No. of Cycles" badge
  const [cyclePopoverAnchor, setCyclePopoverAnchor] = React.useState<{ element: HTMLElement | null; serviceIdx: number | null }>({
    element: null,
    serviceIdx: null,
  });

  const handleCyclePopoverOpen = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    setCyclePopoverAnchor({ element: event.currentTarget, serviceIdx });
  };

  const handleCyclePopoverClose = () => {
    setCyclePopoverAnchor({ element: null, serviceIdx: null });
  };

  const handleCyclePopoverKeyDown = (event: React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCyclePopoverOpen(event, serviceIdx);
    }
  };

  // Memoize header data
  const header = useMemo(() => ({
    billingSource: toLabel(pd.billing_source),
    tariff: toLabel(pd.tariff_name ?? pd.tariff),
    packageName: toLabel(pd.package_name),
    packageAmount: formatCurrency(Number(pd.package_amount ?? 0)),
    packageDuration: toLabel(pd.package_duration),
    durationType: toLabel(convertUnderscoreToTitle(pd.duration_type)),
    distributionType: toLabel(pd.distribution_type),
    serviceAmount: formatCurrency(Number(pd.service_amount ?? 0)),
    pharmacyAmount: formatCurrency(Number(pd.pharmacy_amount ?? 0)),
    servicePct: Number(pd.service_pct ?? pd.service_percent ?? 0),
    pharmacyPct: Number(pd.pharmacy_pct ?? pd.pharmacy_percent ?? 0),
    concessionAmount: formatCurrency(Number(pd.package_concession_amount ?? 0) + Number(pd.total_service_concession_amount ?? 0)),
    netServiceAmount: formatCurrency((Number(pd.service_amount ?? 0)) - (Number(pd.package_concession_amount ?? 0))),
    netAmount: formatCurrency(Number(pd.package_net_amount ?? 0)),
    adjustableHead: pd.adjustable_head ? 'Yes' : null,
    isConsumables: pd.is_consumables ? 'Yes' : null,
    consumableAmount: pd.consumable_amount ? formatCurrency(Number(pd.consumable_amount)) : null,
    remark: pd.remark ?? null,
    finalized: pd.is_finalized,
  }), [pd, formatCurrency]);

  const services = useMemo(() => Array.isArray(data.service_items) ? data.service_items : [], [data.service_items]);
  const pharmacyRaw = useMemo(() => Array.isArray(data.pharmacy_items) ? data.pharmacy_items : [], [data.pharmacy_items]);

  // Pre-compute all service calculations
  const { processedServices, totalServicesAmount } = useMemo(() => {
    const servAmt = Number(pd.service_amount) || 0;
    let total = 0;

    const processed = services.map((s, idx) => {
      const unitRate = Number(s.unit_rate) || 0;
      const quantity = Number(s.quantity) || 0;
      const grossAmount = unitRate * quantity;
      total += grossAmount;

      // Per-unit discount values from the service item
      const discountPerUnit = Number(s.discount_amount) || 0;
      const individualDiscountPerUnit = Number(s.individual_discount_amount) || 0;
      const packageDiscountPerUnit = discountPerUnit - individualDiscountPerUnit;
      
      // Total discounts (per-unit * quantity)
      const totalDiscount = discountPerUnit * quantity;
      const manualDiscount = individualDiscountPerUnit * quantity;
      const packageConcession = packageDiscountPerUnit * quantity;
      
      // Discount percent based on totals
      const discountPercent = grossAmount > 0 ? (totalDiscount / grossAmount) * 100 : 0;
      const serviceAmount = Number(s.total_amount ?? 0);
      const unitPercentage = servAmt > 0 && unitRate > 0 ? ((unitRate * 100) / servAmt) : 0;
      const isUnlimited = s.unlimited_qty || s.unlimited_quantity;

      // Extract procedure_details for "Service From No. of Cycles"
      const procedureDetails = s.procedure_details || [];
      const cycleCount = procedureDetails.length;
      const cycleNames = procedureDetails.map(pd => pd.procedure_source_name || '').filter(Boolean);

      return {
        ...s,
        idx,
        unitRate,
        quantity,
        grossAmount,
        discountAmount: discountPerUnit,
        individualDiscountAmount: individualDiscountPerUnit,
        totalDiscount,
        manualDiscount,
        packageConcession,
        discountPercent,
        serviceAmount,
        unitPercentage,
        isUnlimited,
        cycleCount,
        cycleNames,
        quantityDisplay: isUnlimited ? 'Unlimited' : (s.quantity ?? '-'),
        // Pre-formatted strings
        grossAmountStr: formatCurrency(grossAmount),
        serviceAmountStr: formatCurrency(serviceAmount),
        totalDiscountStr: formatCurrency(totalDiscount),
        manualDiscountStr: formatCurrency(manualDiscount),
        packageConcessionStr: formatCurrency(packageConcession),
        discountPercentStr: discountPercent.toFixed(2),
        unitRateStr: unitRate.toFixed(2),
        unitPercentageStr: unitPercentage.toFixed(2),
        discountAmountPerUnit: discountPerUnit.toFixed(2),
      };
    });

    return { processedServices: processed, totalServicesAmount: total };
  }, [services, pd.service_amount, formatCurrency]);

  // Pre-compute all pharmacy calculations
  const processedPharmacy = useMemo(() => {
    return pharmacyRaw.map((p, idx) => {
      // PharmacyItem may have additional runtime properties from API
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pAny = p as any;
      const unitRate = Number(pAny.unit_rate ?? 0) || 0;
      const quantity = Number(p.quantity) || 0;
      const grossAmount = unitRate * quantity;

      // Per-unit discount values from the pharmacy item (if available)
      const discountPerUnit = Number(pAny.discount_amount ?? 0) || 0;
      const individualDiscountPerUnit = Number(pAny.individual_discount_amount ?? 0) || 0;
      const packageDiscountPerUnit = discountPerUnit - individualDiscountPerUnit;
      
      // Total discounts (per-unit * quantity)
      const totalDiscount = discountPerUnit * quantity;
      const manualDiscount = individualDiscountPerUnit * quantity;
      const packageConcession = packageDiscountPerUnit * quantity;
      
      const discountPercent = grossAmount > 0 ? (totalDiscount / grossAmount) * 100 : 0;
      const netAmount = Number(p.total_amount ?? 0);

      return {
        ...p,
        idx,
        unitRate,
        quantity,
        grossAmount,
        totalDiscount,
        manualDiscount,
        packageConcession,
        discountPercent,
        netAmount,
        // Pre-formatted strings
        grossAmountStr: formatCurrency(grossAmount),
        netAmountStr: formatCurrency(netAmount),
        totalDiscountStr: formatCurrency(totalDiscount),
        manualDiscountStr: formatCurrency(manualDiscount),
        packageConcessionStr: formatCurrency(packageConcession),
        discountPercentStr: discountPercent.toFixed(2),
        unitRateStr: unitRate.toFixed(2),
      };
    });
  }, [pharmacyRaw, formatCurrency]);

  // Memoize header fields

  const headerFields = useMemo(() => [
    { label: tBilling('package_details.billing_source'), value: header.billingSource, size: GRID_SIZES.large },
    { label: tBilling('package_details.tariff'), value: header.tariff, size: GRID_SIZES.large },
    { label: tBilling('package_details.package_name'), value: header.packageName, size: GRID_SIZES.large },
    { label: tBilling('package_details.package_amount'), value: header.packageAmount, size: GRID_SIZES.large },
    { label: tBilling('package_details.package_duration'), value: header.packageDuration, size: GRID_SIZES.large },
    { label: tBilling('package_details.duration_type'), value: header.durationType, size: GRID_SIZES.large },
    { label: tBilling('package_details.distribution_details'), value: header.distributionType, size: GRID_SIZES.large },
    { label: `${tBilling('package_details.service_amount')} (${header.servicePct.toFixed(2)}%)`, value: header.serviceAmount, size: GRID_SIZES.large },
    { label: `${tBilling('package_details.pharmacy_amount')} (${header.pharmacyPct.toFixed(2)}%)`, value: header.pharmacyAmount, size: GRID_SIZES.large },
    { label: tBilling('package_details.net_amount'), value: header.netAmount, size: GRID_SIZES.large },
    { label: tBilling('package_details.remark'), value: header.remark, size: GRID_SIZES.remark, isTruncated: true },
  ].filter(f => f.value !== null && f.value !== undefined && f.value !== '' && f.value !== '-'), [header, tBilling]);

  // Memoize tab button styles
  const getTabSx = useCallback((isActive: boolean) => ({
    ...TAB_BUTTON_BASE_SX,
    color: isActive ? '#e57353' : '#bdbdbd',
    borderColor: isActive ? '#e57353' : '#bdbdbd',
  }), []);

  return (
    <Box data-testid="package-details-view-mode" sx={{ px: 3, py: 1.5 }}>
      <Box data-testid="package-details-view-header" sx={{ display: 'flex', justifyContent: !header.finalized ? 'space-between' : 'flex-start', mb: 1.5 }}>
        <Typography
          variant="h6"
          className={styles.h6Title}
          sx={{ mb: 2 }}
        >        
          {tBilling('package_details.package_details')}
        </Typography>
      
        {!header.finalized &&(
          <IconButton data-testid="package-details-edit-btn" onClick={onBack}>
            <Edit  />
          </IconButton>
        )}
      </Box>


      <Grid2 container spacing={1.5}>
        {headerFields.map((f, idx) => (
          <Grid2 key={idx} size={f.size}>
            <Typography color="text.secondary" className={styles.label}>{f.label}</Typography>
            <Typography className={styles.value}>
              {f.isTruncated ? (
                <TruncatedText text={f.value ?? '-'} title={f.label} iconPosition="left" />
              ) : (
                f.value ?? '-'
              )}
            </Typography>
          </Grid2>
        ))}
      </Grid2>

      <Box><Divider sx={{ my: 2, borderColor: '#afb0b1ff' }} /></Box>


      {/* Services / Pharmacy Items Tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Button
          variant="outlined"
          sx={getTabSx(activeTab === 'services')}
          onClick={() => setActiveTab('services')}
        >
          {tBilling('package_details.services')}
        </Button>
        <Button
          variant="outlined"
          sx={getTabSx(activeTab === 'pharmacy')}
          onClick={() => setActiveTab('pharmacy')}
        >
          {tBilling('package_details.pharmacy_items')}
        </Button>
      </Box>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <Box>
          {/* Procedure Cycles Chips */}
          {pd.procedure_details && pd.procedure_details.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
              {pd.procedure_details.map((proc) => (
                <Chip
                  key={proc.id ?? `${proc.procedure_id}-${proc.procedure_source_type}`}
                  label={proc.procedure_source_name}
                  size="small"
                  sx={{
                    backgroundColor: '#FFF4F2',
                    color: '#000000',
                    fontWeight: 500,
                    px: 1,
                    py: 0.5,
                    fontSize: 14,
                    minWidth: 100,
                    borderRadius: '16px',
                  }}
                />
              ))}
            </Box>
          )}

          <Grid2 container spacing={1.5} sx={{ mb: 2, px: 1 }}>
            <Grid2 size={GRID_SIZES.large}>
              <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.concession_amount', 'Concession Amount')}</Typography>
              <Typography className={styles.value}>{header.concessionAmount}</Typography>
            </Grid2>
            <Grid2 size={GRID_SIZES.large}>
              <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.net_service_amount', 'Net Service Amount')}</Typography>
              <Typography className={styles.value}>{header.netServiceAmount}</Typography>
            </Grid2>
          </Grid2>
          {/* Services Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)', borderRadius: 2, px: 2.5, py: 1, mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#495057' }}>{tBilling('package_details.service_name', 'Service Name')}</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#495057' }}>
              {tBilling('package_details.total_amount', 'Total Amount')}: <Box component="span" sx={{ color: '#2e7d32' }}>{formatCurrency(totalServicesAmount)}</Box>
            </Typography>
          </Box>

          {/* Service Section Summary */}
          <Grid2 container spacing={1.5} sx={{ mb: 2, px: 1 }}>
            <Grid2 size={GRID_SIZES.large}>
              <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.adjustable_head')}</Typography>
              <Typography className={styles.value}>{header.adjustableHead ?? '-'}</Typography>
            </Grid2>
            <Grid2 size={GRID_SIZES.large}>
              <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.is_consumables')}</Typography>
              <Typography className={styles.value}>{header.isConsumables ?? '-'}</Typography>
            </Grid2>
            {pd.is_consumables && (
              <Grid2 size={GRID_SIZES.large}>
                <Typography color="text.secondary" className={styles.label}>{tBilling('package_details.consumable_amount')}</Typography>
                <Typography className={styles.value}>{header.consumableAmount ?? '-'}</Typography>
              </Grid2>
            )}
          </Grid2>
          {processedServices.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>{tBilling('package_details.no_services_added', 'No services added.')}</Typography>
          ) : (
            processedServices.map((s) => (
              <Paper 
                key={s.idx} 
                elevation={0}
                sx={{ borderRadius: 2, mb: 2, overflow: 'hidden', border: '1px solid #e0e0e0', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}
              >
                {/* Service Card Header */}
                <Box sx={{ bgcolor: '#fafafa', px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#e57353', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                      {s.service_name || `Service ${s.idx + 1}`}
                    </Typography>
                    {s.is_prescribed_from_doctor && (
                      <Box sx={{ bgcolor: '#fff3e0', color: '#e65100', px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 600 }}>{tBilling('package_details.rx_short', 'Rx')}</Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {/* Service From No. of Cycles badge */}
                    {s.cycleCount > 0 && (
                      <>
                        <Tooltip title={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}>
                          <Box
                            role="button"
                            tabIndex={0}
                            aria-label={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}
                            onClick={(e) => handleCyclePopoverOpen(e, s.idx)}
                            onKeyDown={(e) => handleCyclePopoverKeyDown(e, s.idx)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: '#e3f2fd',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#bbdefb' },
                              '&:focus': { outline: '2px solid #1976d2', outlineOffset: 2 },
                            }}
                          >
                            <Typography sx={{ fontSize: 12, color: '#1976d2', fontWeight: 500 }}>
                              {tBilling('package_details.service_from_cycles', { defaultValue: 'Service From No. of Cycles' })}:
                            </Typography>
                            <Box
                              sx={{
                                minWidth: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: '#1976d2',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {s.cycleCount}
                            </Box>
                          </Box>
                        </Tooltip>
                        <Popover
                          open={cyclePopoverAnchor.element !== null && cyclePopoverAnchor.serviceIdx === s.idx}
                          anchorEl={cyclePopoverAnchor.element}
                          onClose={handleCyclePopoverClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          PaperProps={{
                            sx: {
                              backgroundColor: '#fff',
                              color: '#222',
                              borderRadius: 2,
                              minWidth: 200,
                              maxWidth: 320,
                              p: 1,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: 12, color: '#1976d2', px: 1, pt: 0.5 }}>
                            {tBilling('package_details.cycle_details', { defaultValue: 'CYCLE DETAILS' })} ({s.cycleCount})
                          </Typography>
                          <List dense sx={{ py: 0 }}>
                            {s.cycleNames.map((name: string, i: number) => (
                              <ListItem key={i} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={name}
                                  primaryTypographyProps={{ sx: { fontSize: 14, color: '#222' } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Popover>
                      </>
                    )}
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 12, color: '#666' }}>{tBilling('package_details.gross', 'Gross')}</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{s.grossAmountStr}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 12, color: '#666' }}>{tBilling('package_details.net', 'Net')}</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#2e7d32' }}>{s.serviceAmountStr}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 12, color: '#666' }}>{tBilling('package_details.discount', 'Discount')}</Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#1565c0' }}>{s.totalDiscountStr} ({s.discountPercentStr}%)</Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Discount Breakdown */}
                {s.manualDiscount > 0 && (
                  <Box sx={{ bgcolor: '#fff8f5', px: 2.5, py: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, borderBottom: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: 12, color: '#e57353', fontWeight: 500 }}>{tBilling('package_details.manual_discount_short', 'Manual Disc')}:</Typography>
                      <Typography sx={{ fontSize: 13, color: '#e57353', fontWeight: 600 }}>{s.manualDiscountStr}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: 12, color: '#e57353', fontWeight: 500 }}>{tBilling('package_details.package_discount_short', 'Package Disc')}:</Typography>
                      <Typography sx={{ fontSize: 13, color: '#e57353', fontWeight: 600 }}>{s.packageConcessionStr}</Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Service Card Body */}
                <Grid2 container sx={{ px: 2, py: 2 }} spacing={2}>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.procedure')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{convertUnderscoreToTitle(s.procedure) || '-'}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.gender')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{toLabel(s.gender)}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.rate_short', 'Rate')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.unitRateStr}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.qty_short', 'Qty')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.quantityDisplay}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.unit_percent_short', 'Unit %')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.unitPercentageStr}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.disc_per_unit_short', 'Disc/Unit')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.discountAmountPerUnit}</Typography>
                  </Grid2>
                </Grid2>
              </Paper>
            ))
          )}
        </Box>
      )}

      {/* Pharmacy Items Tab */}
      {activeTab === 'pharmacy' && (
        <Box>
          {pd.pharmacy_set_all_items ? (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {tBilling('package_details.set_all_items')}: {tBilling('package_details.all_pharmacy_items_available')}
            </Typography>
          ) : processedPharmacy.length === 0 ? (
            <Typography color="text.secondary">{tBilling('package_details.no_pharmacy_items')}</Typography>
          ) : (
            processedPharmacy.map((p) => (
              <Paper 
                key={p.idx} 
                elevation={0}
                sx={{ borderRadius: 2, mb: 2, overflow: 'hidden', border: '1px solid #e0e0e0', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}
              >
                {/* Pharmacy Card Header */}
                <Box sx={{ bgcolor: '#fafafa', px: 2.5, py: 1.25, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#e57353', borderRadius: 1 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                      {p.item_name || `Item ${p.idx + 1}`}
                    </Typography>
                  </Box>
                </Box>
                

                {/* Pharmacy Card Body */}
                <Grid2 container sx={{ px: 2, py: 2 }} spacing={2}>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.quantity')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.quantity}</Typography>
                  </Grid2>
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.uom')}:</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(() => {
                        const matchingConversion = p.uom_conversion?.find((c: { from_uom: number }) => c.from_uom === p.uom);
                        return matchingConversion?.from_uom_label || toLabel(p.uom_description ?? p.uom);
                      })()}
                    </Typography>
                  </Grid2>
               
                  <Grid2 size={GRID_SIZES.cardField}>
                    <Typography variant="body2"><Box component="span" sx={{ color: '#888' }}>{tBilling('package_details.discount', 'Discount')} (%):</Box></Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(p.discount_percent ?? 0).toFixed(2)}</Typography>
                  </Grid2>
                </Grid2>
              </Paper>
            ))
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="outlined" sx={{ minWidth: 140 }} onClick={() => { navigate(`/admin/billing/package/patient-package/${patientId}`, { state: { refetch: true, ts: Date.now() } }); }}>
          {tBilling('counseling_details.back_to_listing')}
        </Button>
      </Box>
    </Box>
  );
};

export default PackageDetailsViewMode;
